import MetricsService from '../../services/MetricsService';
import MetricsHub from '../../services/MetricsHub';
import {
  MetricsSummary,
  MetricDataPoint,
  LogEntry,
  ServiceHealthEntry,
} from '../../models/Metrics';

const MAX_LOG_ENTRIES = 1000;

// Guard against duplicate handler registration on reconnect.
let metricsHandlersRegistered = false;

export interface MetricsState {
  summary: MetricsSummary | null;
  serviceHealth: ServiceHealthEntry[];
  queueDepthHistory: MetricDataPoint[];
  replicasHistory: MetricDataPoint[];
  sullyReplicasHistory: MetricDataPoint[];
  sullyJobsQueuedHistory: MetricDataPoint[];
  sullyJobsRunningHistory: MetricDataPoint[];
  sullyJobsProcessedHistory: MetricDataPoint[];
  sullyJobsNaHistory: MetricDataPoint[];
  rawStorageQueueDepthHistory: MetricDataPoint[];
  rawStorageReplicasHistory: MetricDataPoint[];
  poisonQueueDepthHistory: MetricDataPoint[];
  activePigRunsHistory: MetricDataPoint[];
  activeTcpConnectionsHistory: MetricDataPoint[];
  agmHubConnectionsHistory: MetricDataPoint[];
  streamHubConnectionsHistory: MetricDataPoint[];
  chatHubConnectionsHistory: MetricDataPoint[];
  sqlCpuHistory: MetricDataPoint[];
  sqlDtuHistory: MetricDataPoint[];
  sqlDataReadHistory: MetricDataPoint[];
  sqlLogWriteHistory: MetricDataPoint[];
  logEntries: LogEntry[];
  selectedLogService: string;
  serviceNames: string[];
  isConnected: boolean;
}

export default {
  state: {
    summary: null,
    serviceHealth: [],
    queueDepthHistory: [],
    replicasHistory: [],
    sullyReplicasHistory: [],
    sullyJobsQueuedHistory: [],
    sullyJobsRunningHistory: [],
    sullyJobsProcessedHistory: [],
    sullyJobsNaHistory: [],
    rawStorageQueueDepthHistory: [],
    rawStorageReplicasHistory: [],
    poisonQueueDepthHistory: [],
    activePigRunsHistory: [],
    activeTcpConnectionsHistory: [],
    agmHubConnectionsHistory: [],
    streamHubConnectionsHistory: [],
    chatHubConnectionsHistory: [],
    sqlCpuHistory: [],
    sqlDtuHistory: [],
    sqlDataReadHistory: [],
    sqlLogWriteHistory: [],
    logEntries: [],
    selectedLogService: '',
    serviceNames: [],
    isConnected: false,
  } as MetricsState,
  mutations: {
    SET_METRICS_SUMMARY(state: MetricsState, summary: MetricsSummary) {
      state.summary = summary;
      if (summary.serviceHealth) {
        state.serviceHealth = summary.serviceHealth;
      }
    },
    SET_SERVICE_HEALTH(state: MetricsState, entries: ServiceHealthEntry[]) {
      state.serviceHealth = entries;
    },
    SET_QUEUE_DEPTH_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.queueDepthHistory = dataPoints;
    },
    SET_REPLICAS_HISTORY(state: MetricsState, dataPoints: MetricDataPoint[]) {
      state.replicasHistory = dataPoints;
    },
    SET_SULLY_REPLICAS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sullyReplicasHistory = dataPoints;
    },
    SET_SULLY_JOBS_QUEUED_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sullyJobsQueuedHistory = dataPoints;
    },
    SET_SULLY_JOBS_RUNNING_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sullyJobsRunningHistory = dataPoints;
    },
    SET_SULLY_JOBS_PROCESSED_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sullyJobsProcessedHistory = dataPoints;
    },
    SET_SULLY_JOBS_NA_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sullyJobsNaHistory = dataPoints;
    },
    SET_RAW_STORAGE_QUEUE_DEPTH_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.rawStorageQueueDepthHistory = dataPoints;
    },
    SET_POISON_QUEUE_DEPTH_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.poisonQueueDepthHistory = dataPoints;
    },
    SET_RAW_STORAGE_REPLICAS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.rawStorageReplicasHistory = dataPoints;
    },
    SET_ACTIVE_PIG_RUNS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.activePigRunsHistory = dataPoints;
    },
    SET_ACTIVE_TCP_CONNECTIONS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.activeTcpConnectionsHistory = dataPoints;
    },
    SET_AGM_HUB_CONNECTIONS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.agmHubConnectionsHistory = dataPoints;
    },
    SET_STREAM_HUB_CONNECTIONS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.streamHubConnectionsHistory = dataPoints;
    },
    SET_CHAT_HUB_CONNECTIONS_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.chatHubConnectionsHistory = dataPoints;
    },
    SET_SQL_CPU_HISTORY(state: MetricsState, dataPoints: MetricDataPoint[]) {
      state.sqlCpuHistory = dataPoints;
    },
    SET_SQL_DTU_HISTORY(state: MetricsState, dataPoints: MetricDataPoint[]) {
      state.sqlDtuHistory = dataPoints;
    },
    SET_SQL_DATA_READ_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sqlDataReadHistory = dataPoints;
    },
    SET_SQL_LOG_WRITE_HISTORY(
      state: MetricsState,
      dataPoints: MetricDataPoint[]
    ) {
      state.sqlLogWriteHistory = dataPoints;
    },
    APPEND_LOG_ENTRIES(state: MetricsState, entries: LogEntry[]) {
      state.logEntries = state.logEntries.concat(entries);
      if (state.logEntries.length > MAX_LOG_ENTRIES) {
        state.logEntries.splice(0, state.logEntries.length - MAX_LOG_ENTRIES);
      }
    },
    APPEND_LOG_ENTRY(state: MetricsState, entry: LogEntry) {
      state.logEntries.push(entry);
      if (state.logEntries.length > MAX_LOG_ENTRIES) {
        state.logEntries.splice(0, state.logEntries.length - MAX_LOG_ENTRIES);
      }
    },
    SET_SELECTED_LOG_SERVICE(state: MetricsState, serviceName: string) {
      state.selectedLogService = serviceName;
    },
    SET_METRICS_CONNECTED(state: MetricsState, isConnected: boolean) {
      state.isConnected = isConnected;
    },
    SET_SERVICE_NAMES(state: MetricsState, names: string[]) {
      state.serviceNames = names;
    },
    CLEAR_METRICS(state: MetricsState) {
      state.summary = null;
      state.serviceHealth = [];
      state.queueDepthHistory = [];
      state.replicasHistory = [];
      state.sullyReplicasHistory = [];
      state.sullyJobsQueuedHistory = [];
      state.sullyJobsRunningHistory = [];
      state.sullyJobsProcessedHistory = [];
      state.sullyJobsNaHistory = [];
      state.rawStorageQueueDepthHistory = [];
      state.rawStorageReplicasHistory = [];
      state.poisonQueueDepthHistory = [];
      state.activePigRunsHistory = [];
      state.activeTcpConnectionsHistory = [];
      state.agmHubConnectionsHistory = [];
      state.streamHubConnectionsHistory = [];
      state.chatHubConnectionsHistory = [];
      state.sqlCpuHistory = [];
      state.sqlDtuHistory = [];
      state.sqlDataReadHistory = [];
      state.sqlLogWriteHistory = [];
      state.logEntries = [];
      state.selectedLogService = '';
      state.isConnected = false;
    },
    CLEAR_LOG_ENTRIES(state: MetricsState) {
      state.logEntries = [];
    },
  },
  actions: {
    fetchMetricsSummary(context: any) {
      MetricsService.getSummary()
        .then((response) => {
          context.commit('SET_METRICS_SUMMARY', response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchQueueDepthHistory(context: any, lookback: string) {
      MetricsService.getQueueDepth(lookback)
        .then((response) => {
          context.commit('SET_QUEUE_DEPTH_HISTORY', response.data.dataPoints);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchReplicasHistory(context: any, lookback: string) {
      MetricsService.getReplicas(lookback)
        .then((response) => {
          context.commit('SET_REPLICAS_HISTORY', response.data.dataPoints);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchSullyReplicasHistory(context: any, lookback: string) {
      MetricsService.getSullyReplicas(lookback)
        .then((response) => {
          context.commit(
            'SET_SULLY_REPLICAS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchRawStorageQueueDepthHistory(context: any, lookback: string) {
      MetricsService.getRawStorageQueueDepth(lookback)
        .then((response) => {
          context.commit(
            'SET_RAW_STORAGE_QUEUE_DEPTH_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchPoisonQueueDepthHistory(context: any, lookback: string) {
      MetricsService.getPoisonQueueDepth(lookback)
        .then((response) => {
          context.commit(
            'SET_POISON_QUEUE_DEPTH_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchRawStorageReplicasHistory(context: any, lookback: string) {
      MetricsService.getRawStorageReplicas(lookback)
        .then((response) => {
          context.commit(
            'SET_RAW_STORAGE_REPLICAS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchActivePigRunsHistory(context: any, lookback: string) {
      MetricsService.getActivePigRuns(lookback)
        .then((response) => {
          context.commit(
            'SET_ACTIVE_PIG_RUNS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchActiveTcpConnectionsHistory(context: any, lookback: string) {
      MetricsService.getActiveTcpConnections(lookback)
        .then((response) => {
          context.commit(
            'SET_ACTIVE_TCP_CONNECTIONS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchAgmHubConnectionsHistory(context: any, lookback: string) {
      MetricsService.getAgmHubConnections(lookback)
        .then((response) => {
          context.commit(
            'SET_AGM_HUB_CONNECTIONS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchStreamHubConnectionsHistory(context: any, lookback: string) {
      MetricsService.getStreamHubConnections(lookback)
        .then((response) => {
          context.commit(
            'SET_STREAM_HUB_CONNECTIONS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchChatHubConnectionsHistory(context: any, lookback: string) {
      MetricsService.getChatHubConnections(lookback)
        .then((response) => {
          context.commit(
            'SET_CHAT_HUB_CONNECTIONS_HISTORY',
            response.data.dataPoints
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchSullyJobsHistory(
      context: any,
      payload: { lookback: string; metric: string; mutation: string }
    ) {
      MetricsService.getSullyJobs(payload.lookback, payload.metric)
        .then((response) => {
          context.commit(payload.mutation, response.data.dataPoints);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchAllSullyJobsHistory(context: any, lookback: string) {
      const jobs: { metric: string; mutation: string }[] = [
        {
          metric: 'sully-jobs-queued',
          mutation: 'SET_SULLY_JOBS_QUEUED_HISTORY',
        },
        {
          metric: 'sully-jobs-running',
          mutation: 'SET_SULLY_JOBS_RUNNING_HISTORY',
        },
        {
          metric: 'sully-jobs-processed',
          mutation: 'SET_SULLY_JOBS_PROCESSED_HISTORY',
        },
        {
          metric: 'sully-jobs-na',
          mutation: 'SET_SULLY_JOBS_NA_HISTORY',
        },
      ];
      Promise.all(
        jobs.map((j) =>
          MetricsService.getSullyJobs(lookback, j.metric).then((response) => {
            context.commit(j.mutation, response.data.dataPoints);
          })
        )
      ).catch((error) => {
        console.error(error);
      });
    },
    fetchSqlHistory(
      context: any,
      payload: { lookback: string; metric: string; mutation: string }
    ) {
      MetricsService.getSqlMetrics(payload.lookback, payload.metric)
        .then((response) => {
          context.commit(payload.mutation, response.data.dataPoints);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchAllSqlHistory(context: any, lookback: string) {
      const metrics: { metric: string; mutation: string }[] = [
        { metric: 'sql-cpu-percent', mutation: 'SET_SQL_CPU_HISTORY' },
        { metric: 'sql-dtu-percent', mutation: 'SET_SQL_DTU_HISTORY' },
        {
          metric: 'sql-data-read-percent',
          mutation: 'SET_SQL_DATA_READ_HISTORY',
        },
        {
          metric: 'sql-log-write-percent',
          mutation: 'SET_SQL_LOG_WRITE_HISTORY',
        },
      ];
      Promise.all(
        metrics.map((m) =>
          MetricsService.getSqlMetrics(lookback, m.metric).then((response) => {
            context.commit(m.mutation, response.data.dataPoints);
          })
        )
      ).catch((error) => {
        console.error(error);
      });
    },
    fetchServiceNames(context: any) {
      MetricsService.getServices()
        .then((response) => {
          context.commit('SET_SERVICE_NAMES', response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchServiceHealth(context: any) {
      MetricsService.getServiceHealth()
        .then((response) => {
          context.commit('SET_SERVICE_HEALTH', response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchLogs(context: any, payload: { serviceName: string; count: number }) {
      MetricsService.getLogs(payload.serviceName, payload.count)
        .then((response) => {
          context.commit('APPEND_LOG_ENTRIES', response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    initMetricsHub(context: any) {
      MetricsHub.init();
      const connection = MetricsHub.getConnection();

      context.commit('SET_METRICS_CONNECTED', true);

      // Skip re-registration if handlers are already wired up.
      if (metricsHandlersRegistered) {
        return;
      }
      metricsHandlersRegistered = true;

      connection.on('SendMetricsUpdate', (data: MetricsSummary) => {
        context.commit('SET_METRICS_SUMMARY', data);
      });

      connection.on('ReceiveLogEntry', (entry: LogEntry) => {
        context.commit('APPEND_LOG_ENTRY', entry);
      });

      connection.on(
        'SendLogEntries',
        (_service: string, entries: LogEntry[]) => {
          context.commit('APPEND_LOG_ENTRIES', entries);
        }
      );
    },
    disconnectMetricsHub(context: any) {
      MetricsHub.disconnect();
      metricsHandlersRegistered = false;
      context.commit('SET_METRICS_CONNECTED', false);
    },
    subscribeToLogs(context: any, serviceName: string) {
      const connection = MetricsHub.getConnection();
      if (connection) {
        connection.invoke('SubscribeToLogs', serviceName);
        context.commit('SET_SELECTED_LOG_SERVICE', serviceName);
      }
    },
    unsubscribeFromLogs(context: any) {
      const connection = MetricsHub.getConnection();
      const current = context.state.selectedLogService;
      if (connection && current) {
        connection.invoke('UnsubscribeFromLogs', current);
      }
      context.commit('SET_SELECTED_LOG_SERVICE', '');
    },
  },
};
